// Promise-based pickers, the web version of the Android Sheet/DaySheet: a list to choose from,
// a day (the next days with one tap, or any day on a calendar) and a text prompt.
import React, { useCallback, useRef, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemIcon, ListItemText,
  TextField, Typography,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import * as T from '../types';
import { addDays, dayLabel, today } from '../labels';

export interface SheetOption {
  value: string;
  text: string;
  detail?: string;
  icon?: React.ReactNode;
  /** Highlighted (the main action, or the current choice). */
  primary?: boolean;
  danger?: boolean;
}

type State =
  | { type: 'list'; title: string; subtitle?: string | null; options: SheetOption[]; resolve: (value: string | null) => void }
  | { type: 'date'; title: string; value: T.DateOnly; min?: T.DateOnly; resolve: (value: T.DateOnly | null) => void }
  | { type: 'text'; title: string; label: string; value: string; resolve: (value: string | null) => void };

const DAYS_SHOWN = 8;

/** "Hoje", "Amanhã", then "Quinta-feira, 23 out". */
export function dayOptionLabel(day: T.DateOnly): string {
  const now = today();
  if (day === now) return 'Hoje';
  if (day === addDays(now, 1)) return 'Amanhã';
  return dayLabel(day);
}

export interface Sheets {
  /** The value of the option chosen, or null when cancelled. */
  choose: (title: string, options: SheetOption[], subtitle?: string | null) => Promise<string | null>;
  /** Today and the next days, plus "Outro dia…" with a calendar; null when cancelled. */
  chooseDay: (title: string, current: T.DateOnly, subtitle?: string | null) => Promise<T.DateOnly | null>;
  /** A line of text, or null when cancelled or empty. */
  prompt: (title: string, label: string, initial?: string) => Promise<string | null>;
  element: React.ReactElement;
}

/** `const sheets = useSheets();` then `await sheets.choose(...)` and render `{sheets.element}`. */
export function useSheets(): Sheets {
  const [state, setState] = useState<State | null>(null);
  const [text, setText] = useState('');
  const stateRef = useRef<State | null>(null);
  stateRef.current = state;

  const choose = useCallback((title: string, options: SheetOption[], subtitle?: string | null) =>
    new Promise<string | null>(resolve => setState({ type: 'list', title, subtitle, options, resolve })), []);

  const pickDate = useCallback((title: string, value: T.DateOnly, min?: T.DateOnly) =>
    new Promise<T.DateOnly | null>(resolve => { setText(value); setState({ type: 'date', title, value, min, resolve }); }), []);

  const chooseDay = useCallback(async (title: string, current: T.DateOnly, subtitle?: string | null) => {
    const now = today();
    const options: SheetOption[] = Array.from({ length: DAYS_SHOWN }, (_, i) => addDays(now, i)).map(d => ({
      value: d, text: dayOptionLabel(d), primary: d === current,
    }));
    options.push({ value: 'other', text: 'Outro dia…', detail: 'Escolher no calendário', icon: <EventIcon /> });
    const choice = await choose(title, options, subtitle);
    if (choice === null) return null;
    if (choice !== 'other') return choice;
    return pickDate(title, current < now ? now : current, now);
  }, [choose, pickDate]);

  const prompt = useCallback((title: string, label: string, initial = '') =>
    new Promise<string | null>(resolve => { setText(initial); setState({ type: 'text', title, label, value: initial, resolve }); }), []);

  const close = (value: string | null) => {
    const current = stateRef.current;
    setState(null);
    current?.resolve(value as any);
  };

  const submitText = () => {
    const value = text.trim();
    close(value.length > 0 ? value : null);
  };

  const element = (
    <>
      <Dialog open={state?.type === 'list'} onClose={() => close(null)} maxWidth="xs" fullWidth>
        {state?.type === 'list' && (
          <>
            <DialogTitle sx={{ pb: state.subtitle ? 0 : undefined }}>{state.title}</DialogTitle>
            {state.subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1, whiteSpace: 'pre-line' }}>{state.subtitle}</Typography>
            )}
            <List sx={{ pt: 0 }}>
              {state.options.map(o => (
                <ListItemButton key={o.value} onClick={() => close(o.value)} selected={o.primary}>
                  {o.icon && <ListItemIcon sx={{ minWidth: 40, color: o.danger ? 'error.main' : o.primary ? 'primary.main' : undefined }}>{o.icon}</ListItemIcon>}
                  <ListItemText
                    primary={o.text}
                    secondary={o.detail}
                    slotProps={{ primary: { sx: { color: o.danger ? 'error.main' : o.primary ? 'primary.main' : undefined, fontWeight: o.primary ? 600 : undefined } } }}
                  />
                </ListItemButton>
              ))}
            </List>
            <DialogActions><Button onClick={() => close(null)}>Cancelar</Button></DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={state?.type === 'date' || state?.type === 'text'} onClose={() => close(null)} maxWidth="xs" fullWidth>
        {state?.type === 'date' && (
          <>
            <DialogTitle>{state.title}</DialogTitle>
            <DialogContent>
              <TextField
                type="date" fullWidth value={text} onChange={e => setText(e.target.value)} sx={{ mt: 1 }}
                slotProps={{ htmlInput: { min: state.min } }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => close(null)}>Cancelar</Button>
              <Button variant="contained" disabled={!text} onClick={() => close(text)}>Escolher</Button>
            </DialogActions>
          </>
        )}
        {state?.type === 'text' && (
          <>
            <DialogTitle>{state.title}</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus fullWidth label={state.label} value={text} sx={{ mt: 1 }}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitText(); } }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => close(null)}>Cancelar</Button>
              <Button variant="contained" onClick={submitText}>OK</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );

  return { choose, chooseDay, prompt, element };
}
